/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import cx from 'classnames';
import { get, isEmpty } from 'lodash';

import { metadata, unstable } from '@broadleaf/admin-components';
import PropTypes from 'prop-types';
import messages from './AuroraOrderTotals.messages';
import { FormattedMessage } from 'react-intl';

const { CommonProps } = metadata.views.SummaryOrderViewSection;
const { Card, Currency, useFormatMessage } = unstable;

export const AuroraOrderTotals = ({ className, metadata, state }) => {
  const formatMessage = useFormatMessage();
  const adjustmentsTotal = state.data.orderPricing.adjustmentsTotal;
  const hasAdjustments = !!get(adjustmentsTotal, 'amount', 0);
  const coreCostTotal = state.data.coreCostTotal;
  const hasCoreCost = !!get(coreCostTotal, 'amount', 0);
  const adjustments = get(state, 'data.adjustments', []);
  const hasPromotions = !isEmpty(adjustments);

  return (
    <>
      <Card className={className}>
        <Card.Header>
          <Card.Header.Title title={metadata.label}>
            {metadata.label}
          </Card.Header.Title>
        </Card.Header>
        <Card.Body className="tw-flex tw-flex-col">
          <TotalDetail
            className="tw-mb-3"
            label={<FormattedMessage id="OrderView.summary.subtotal" />}
          >
            <TotalPrice {...state.data.orderPricing.subtotal} />
          </TotalDetail>
          {hasCoreCost && (
            <TotalDetail
              className="tw-mb-3"
              label={formatMessage(messages.coreCostTotal)}
            >
              <TotalPrice {...coreCostTotal} />
            </TotalDetail>
          )}
          {hasAdjustments && (
            <TotalDetail
              className="tw-mb-3"
              label={
                <FormattedMessage id="OrderView.summary.adjustmentsTotal" />
              }
            >
              <TotalPrice isDiscount {...adjustmentsTotal} />
            </TotalDetail>
          )}
          <TotalDetail
            className="tw-mb-3"
            label={<FormattedMessage id="OrderView.summary.fulfillmentTotal" />}
          >
            <TotalPrice {...state.data.orderPricing.fulfillmentTotal} />
          </TotalDetail>
          <TotalDetail
            className="tw-mb-3"
            label={<FormattedMessage id="OrderView.summary.totalTax" />}
          >
            <TotalPrice {...state.data.orderPricing.totalTax} />
          </TotalDetail>
          <hr className="tw-mb-3" />
          <GrandTotalDetail
            label={<FormattedMessage id="OrderView.summary.total" />}
          >
            <TotalPrice {...state.data.orderPricing.total} />
          </GrandTotalDetail>
        </Card.Body>
      </Card>

      {hasPromotions && (
        <Card className={`${className} tw-mt-4`}>
          <Card.Header>
            <Card.Header.Title title="Promotions">Promotions</Card.Header.Title>
          </Card.Header>
          <Card.Body className="tw-flex tw-flex-col">
            {adjustments.map((adjustment, index) => {
              const promoName = get(adjustment, 'offerRef.name', '---');
              const promoCode = get(adjustment, 'codeUsed', '---');
              return (
                <React.Fragment key={index}>
                  <TotalDetail
                    className="tw-mb-1"
                    label={formatMessage(messages.promoName)}
                  >
                    {promoName}
                  </TotalDetail>
                  <TotalDetail
                    className="tw-mb-3"
                    label={formatMessage(messages.promoCode)}
                  >
                    {promoCode}
                  </TotalDetail>
                  {index < adjustments.length - 1 && <hr className="tw-mb-3" />}
                </React.Fragment>
              );
            })}
          </Card.Body>
        </Card>
      )}
    </>
  );
};

AuroraOrderTotals.propTypes = {
  ...CommonProps,
  className: PropTypes.string
};

const TotalDetail = ({ children, className, label }) => {
  return (
    <div
      className={cx(
        className,
        'tw-flex-no-wrap tw-flex tw-flex-row tw-justify-between'
      )}
    >
      <div className="tw-flex-initial tw-truncate tw-text-sm tw-text-gray-700">
        {label}
      </div>
      <div className="tw-whitespace-no-wrap tw-flex-1 tw-text-right tw-text-sm tw-font-medium tw-text-gray-800">
        {children}
      </div>
    </div>
  );
};

const GrandTotalDetail = ({ children, className, label }) => {
  return (
    <div
      className={cx(
        className,
        'tw-flex-no-wrap tw-flex tw-flex-row tw-justify-between'
      )}
    >
      <div className="tw-flex-initial tw-truncate tw-font-medium tw-text-gray-800">
        {label}
      </div>
      <div className="tw-whitespace-no-wrap tw-flex-1 tw-text-right tw-font-semibold tw-text-gray-900">
        {children}
      </div>
    </div>
  );
};

const TotalPrice = ({ amount, currency, isDiscount }) => {
  return (
    <div>
      {isDiscount && <span className="tw-mr-1">{'-'}</span>}
      <Currency currency={currency} value={amount} />
    </div>
  );
};

AuroraOrderTotals.TYPE = 'ORDER_SUMMARY_TOTALS';
export default AuroraOrderTotals;
